import { useEffect, useState } from 'react';
import MaterielCommercePicker from './MaterielCommercePicker';
import { TYPE_LABELS } from '../../materiels/materielDisplayMaps';
import { friendlyMaterielActionError } from '../../materiels/materielActionErrors';

const TYPES = Object.keys(TYPE_LABELS);

/**
 * Modal Ajouter — stock par défaut ou créer et affecter.
 */
export default function MaterielAddModal({
  open,
  onClose,
  onSubmit,
  commerces = [],
  commercesLoading = false,
  commercesError = null,
  onRetryCommerces,
}) {
  const [type, setType] = useState('PRINTER_CLOUD');
  const [serialNumber, setSerialNumber] = useState('');
  const [model, setModel] = useState('');
  const [label, setLabel] = useState('');
  const [assignNow, setAssignNow] = useState(false);
  const [commerce, setCommerce] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (!open) return;
    setType('PRINTER_CLOUD');
    setSerialNumber('');
    setModel('');
    setLabel('');
    setAssignNow(false);
    setCommerce(null);
    setSubmitting(false);
    setFieldError('');
  }, [open]);

  if (!open) return null;

  const primaryLabel = assignNow ? 'Créer et affecter' : 'Ajouter au stock';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const sn = serialNumber.trim();
    if (!sn) {
      setFieldError('Indiquez le numéro de série.');
      return;
    }
    if (!type) {
      setFieldError('Choisissez un type.');
      return;
    }
    if (assignNow && !commerce?.id) {
      setFieldError('Sélectionnez un point de vente.');
      return;
    }
    setFieldError('');
    setSubmitting(true);
    try {
      await onSubmit?.({
        serialNumber: sn,
        type,
        model: model.trim() || null,
        label: label.trim() || null,
        assignNow,
        commerce,
      });
    } catch (err) {
      setFieldError(
        friendlyMaterielActionError(
          err,
          'Impossible d’enregistrer le matériel. Réessayez.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const requestClose = () => {
    if (submitting) return;
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Fermer"
        disabled={submitting}
        onClick={requestClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="materiel-add-title"
        className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto"
      >
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="flex justify-between items-start gap-2">
            <h2
              id="materiel-add-title"
              className="text-base font-semibold text-gray-900"
            >
              Ajouter un matériel
            </h2>
            <button
              type="button"
              onClick={requestClose}
              disabled={submitting}
              className="text-xs text-gray-500 hover:underline"
            >
              Fermer
            </button>
          </div>

          <div className="space-y-3">
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">Type *</span>
              <select
                value={type}
                disabled={submitting}
                onChange={(e) => setType(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">
                Numéro de série *
              </span>
              <input
                required
                value={serialNumber}
                disabled={submitting}
                onChange={(e) => setSerialNumber(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                autoComplete="off"
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">Modèle</span>
              <input
                value={model}
                disabled={submitting}
                onChange={(e) => setModel(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-gray-600">Libellé</span>
              <input
                value={label}
                disabled={submitting}
                onChange={(e) => setLabel(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </label>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-gray-600">
              Destination
            </legend>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="dest"
                checked={!assignNow}
                disabled={submitting}
                onChange={() => {
                  setAssignNow(false);
                  setCommerce(null);
                }}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-gray-900">Mettre en stock</span>
                <span className="block text-xs text-gray-500">
                  Recommandé — affectation plus tard
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="dest"
                checked={assignNow}
                disabled={submitting}
                onChange={() => setAssignNow(true)}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-gray-900">
                  Affecter maintenant
                </span>
                <span className="block text-xs text-gray-500">
                  Choisir un point de vente par son nom
                </span>
              </span>
            </label>
          </fieldset>

          {assignNow ? (
            <MaterielCommercePicker
              commerces={commerces}
              value={commerce}
              onChange={setCommerce}
              disabled={submitting}
              loading={commercesLoading}
              error={commercesError}
              onRetry={onRetryCommerces}
            />
          ) : null}

          {fieldError ? (
            <p className="text-sm text-red-700" role="alert">
              {fieldError}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={requestClose}
              disabled={submitting}
              className="px-3 py-2 text-sm text-gray-700 hover:underline"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {submitting ? 'Enregistrement…' : primaryLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
